import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import AppRoutes from './Routes/Routes';
import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import Sidebar from './components/Sidebar/Sidebar';
import Poster from './components/Poster/Poster';
import HomeCategories from './components/HomePage/HomeCategories';
import HomeTopProducts from './components/HomePage/HomeTopProducts';
import Preloader from './components/Preloader/Preloader';
import { getCategories } from './Store/categories/categoriesSlice';
import { getProducts } from './Store/products/productsSlice';
import { getContacts } from './Store/contacts/contactsSlice';
import { initAuth } from './Store/user/userSlice';
import UserForm from './User/UserForm';
import { applyHomeSeo, applyNoIndexSeo } from './utils/seo';
import { initAnalytics, trackPageView } from './utils/analytics';

function App() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { sessionInitialized } = useSelector((state) => state.user);
  const categoriesLoading = useSelector((state) => state.categories.isLoading);
  const productsLoading = useSelector((state) => state.products.isLoading);
  const contactsLoading = useSelector((state) => state.contacts.isLoading);
  const [showBootPreloader, setShowBootPreloader] = useState(true);
  const [bootPreloaderExiting, setBootPreloaderExiting] = useState(false);
  const isBooting = !sessionInitialized || categoriesLoading || productsLoading || contactsLoading;
  const isAdminPage = location.pathname === '/admin';
  const isHomePage = location.pathname === '/';
  const isCategoryPage = /^\/categories\/[^/]+/.test(location.pathname);
  const isProductPage = /^\/products\/[^/]+/.test(location.pathname);
  const showPoster = isHomePage || isCategoryPage;
  const showHeroTopGroup = showPoster || isHomePage;

  useEffect(() => {
    dispatch(initAuth());
    initAnalytics();
  }, [dispatch]);

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  useEffect(() => {
    dispatch(getCategories());
    dispatch(getProducts());
    dispatch(getContacts());
  }, [dispatch]);

  useEffect(() => {
    if (isBooting) return undefined;

    setBootPreloaderExiting(true);
    const timer = setTimeout(() => setShowBootPreloader(false), 450);
    return () => clearTimeout(timer);
  }, [isBooting]);

  useEffect(() => {
    const path = location.pathname;
    const noIndexRoutes = ['/cart', '/checkout', '/favorites', '/orders', '/search'];
    if (path === '/admin' || noIndexRoutes.includes(path)) {
      applyNoIndexSeo();
      return;
    }
    if (path === '/') {
      applyHomeSeo();
    }
  }, [location.pathname]);

  if (isAdminPage) {
    return (
      <>
        {showBootPreloader && (
          <Preloader fullScreen exiting={bootPreloaderExiting} message="Запуск двигуна…" />
        )}
        <AppRoutes />
      </>
    );
  }

  return (
    <>
      {showBootPreloader && (
        <Preloader fullScreen exiting={bootPreloaderExiting} message="Запуск двигуна…" />
      )}
    <div className="app">
      <Header />
      <UserForm />
      <div
        className={`container${showPoster ? ' containerWithHero' : ''}${isHomePage ? ' containerHome' : ''}${isProductPage ? ' containerProduct' : ''}`}
      >
        <Sidebar />
        <div className="mainColumn">
          {showHeroTopGroup && (
            <div className="heroTopGroup">
              {showPoster && (
                <div className="heroSlot">
                  <Poster />
                </div>
              )}
              {isHomePage && (
                <div className="heroCategoriesSlot">
                  <HomeCategories />
                </div>
              )}
            </div>
          )}
          {isHomePage && (
            <div className="heroTopProductsSlot">
              <HomeTopProducts />
            </div>
          )}
          <main className="mainContent">
            <AppRoutes />
          </main>
        </div>
      </div>
      <Footer />
    </div>
    </>
  );
}

export default App;
